import Vue from "vue";
import { getTenantHeaderValue } from "./tenant";

/*
 * SaaS plan entitlements (SAAS-REVAMP/DESIGN.md §4.6, §8.5).
 *
 * The SaaS status response (App.vue poll + sign-in) carries `entitlements`: a snapshot of what the
 * tenant's plan includes. It is kept in localStorage, tagged with the tenant, so the first paint,
 * the navbar and the router guard can read it synchronously (a record stored for another tenant —
 * dev/staging tenant overrides — is ignored); a change fires ENTITLEMENTS_EVENT on window.
 *
 * FAIL-OPEN: with no usable snapshot (a SaaS backend that does not send one yet, a format version
 * this build does not know, nothing stored yet) every feature reads as included and every limit
 * as unlimited, so nothing changes for a tenant until the backend ships the snapshot.
 *
 * `supported` says whether the latest status response carried the `entitlements` key at all (even
 * as null): the sign that the SaaS backend serves plans, which the My plan page needs.
 */
export const ENTITLEMENTS_STORAGE_KEY = "SAAS_ENTITLEMENTS";
export const ENTITLEMENTS_EVENT = "bizo:entitlements";
const SNAPSHOT_VERSION = 1;

export const entitlementsState = Vue.observable({
  supported: false, // the SaaS backend sends entitlements (My plan has something to show)
  loaded: false, // a valid (v === 1) snapshot is applied
  rev: 0,
  plan: null, // { id, code, name } | null
  state: null, // trial | active | pending_payment | expired | cancelled | none
  suspended: false,
  features: new Set(), // replaced (never mutated) on change, so templates re-render
  limits: {}, // { max_branches: 3, ... } — absent key = unlimited
  flags: [],
  customized: false,
  version: 0, // bumped on every change; App.vue re-keys the navbar on it
});

let currentSignature = "none";

/** A usable snapshot in normalised form, or null (anything but `v === 1` is ignored). */
export function normalizeSnapshot(raw) {
  if (!raw || typeof raw !== "object" || raw.v !== SNAPSHOT_VERSION || !Array.isArray(raw.features)) {
    return null;
  }
  const plan = raw.plan && typeof raw.plan === "object" ? { id: raw.plan.id || null, code: raw.plan.code || null, name: raw.plan.name || null } : null;
  const limits = {};
  if (raw.limits && typeof raw.limits === "object" && !Array.isArray(raw.limits)) {
    Object.keys(raw.limits).forEach(key => {
      const max = raw.limits[key];
      // null / missing = unlimited, so only real numbers are kept
      if (typeof max === "number" && isFinite(max) && max >= 0) limits[key] = max;
    });
  }
  return {
    rev: Number(raw.rev) || 0,
    plan,
    state: typeof raw.state === "string" ? raw.state : null,
    suspended: raw.suspended === true,
    features: raw.features.filter(key => typeof key === "string"),
    limits,
    flags: Array.isArray(raw.flags) ? raw.flags.filter(key => typeof key === "string") : [],
    customized: raw.customized === true,
  };
}

function signatureOf(supported, snapshot) {
  if (!snapshot) return supported ? "supported" : "none";
  const limits = Object.keys(snapshot.limits)
    .sort()
    .map(key => key + "=" + snapshot.limits[key]);
  return JSON.stringify([
    snapshot.rev,
    snapshot.plan,
    snapshot.state,
    snapshot.suspended,
    snapshot.features.slice().sort(),
    limits,
    snapshot.flags.slice().sort(),
    snapshot.customized,
  ]);
}

function currentTenant() {
  try {
    return String(getTenantHeaderValue() || "");
  } catch (e) {
    return "";
  }
}

function writeStorage(supported, raw) {
  try {
    if (supported) {
      localStorage.setItem(ENTITLEMENTS_STORAGE_KEY, JSON.stringify({ tenant: currentTenant(), supported: true, snapshot: raw || null }));
    } else {
      localStorage.removeItem(ENTITLEMENTS_STORAGE_KEY);
    }
  } catch (e) {
    /* storage unavailable: the in-memory copy still applies */
  }
}

function notify() {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  try {
    window.dispatchEvent(
      new CustomEvent(ENTITLEMENTS_EVENT, {
        detail: { supported: entitlementsState.supported, loaded: entitlementsState.loaded, rev: entitlementsState.rev, version: entitlementsState.version },
      })
    );
  } catch (e) {
    /* CustomEvent unavailable: listeners simply miss this change */
  }
}

/**
 * Puts `raw` (a snapshot or null) in force; `supported` = the response carried the entitlements key
 * (a usable snapshot implies it). Returns true when what the app sees changed.
 */
function setState(supported, raw, { persist = true, emit = true } = {}) {
  const snapshot = normalizeSnapshot(raw);
  const live = !!supported || !!snapshot;
  if (persist) writeStorage(live, snapshot ? raw : null);
  const signature = signatureOf(live, snapshot);
  if (signature === currentSignature) return false;
  currentSignature = signature;
  entitlementsState.supported = live;
  entitlementsState.loaded = !!snapshot;
  entitlementsState.rev = snapshot ? snapshot.rev : 0;
  entitlementsState.plan = snapshot ? snapshot.plan : null;
  entitlementsState.state = snapshot ? snapshot.state : null;
  entitlementsState.suspended = snapshot ? snapshot.suspended : false;
  entitlementsState.features = new Set(snapshot ? snapshot.features : []);
  entitlementsState.limits = snapshot ? snapshot.limits : {};
  entitlementsState.flags = snapshot ? snapshot.flags : [];
  entitlementsState.customized = snapshot ? snapshot.customized : false;
  entitlementsState.version += 1;
  if (emit) notify();
  return true;
}

/**
 * Applies the snapshot carried by a SaaS status response (or any response shaped
 * `{ entitlements }`). A response without `entitlements`, or with null, leaves no snapshot —
 * i.e. no gating. A missing response (failed call) changes nothing.
 * @returns {boolean} true when the entitlements the app sees changed
 */
export function applyEntitlements(statusData) {
  if (!statusData || typeof statusData !== "object") return false;
  return setState(Object.prototype.hasOwnProperty.call(statusData, "entitlements"), statusData.entitlements);
}

/** Drops the snapshot (logout). */
export function clearEntitlements() {
  return setState(false, null);
}

/** True once a status response showed that the SaaS backend serves plans (gates My plan). */
export function hasPlanBackend() {
  return entitlementsState.supported;
}

/** Is `key` included in the plan? Always true without a snapshot (fail-open). */
export function hasFeature(key) {
  if (!entitlementsState.loaded || !key) return true;
  return entitlementsState.features.has(key);
}

/**
 * The limit that applies for `key` given the plan's features and limits; null = unlimited.
 * Without `multi_branch` a tenant runs one branch whatever max_branches says (DESIGN §2.3).
 * `features` may be a Set or an array of keys.
 */
export function effectiveLimit(features, limits, key) {
  let max = limits && typeof limits[key] === "number" ? limits[key] : null;
  if (key === "max_branches") {
    const multiBranch = features instanceof Set ? features.has("multi_branch") : Array.isArray(features) && features.indexOf("multi_branch") > -1;
    if (!multiBranch) max = max === null ? 1 : Math.min(max, 1);
  }
  return max;
}

/** The plan's limit for `key` (null = unlimited). Always null without a snapshot. */
export function limitOf(key) {
  if (!entitlementsState.loaded) return null;
  return effectiveLimit(entitlementsState.features, entitlementsState.limits, key);
}

/** True when a snapshot says the account is suspended. */
export function isPlanSuspended() {
  return entitlementsState.loaded && entitlementsState.suspended;
}

/**
 * The first plan feature a resolved route needs but the plan lacks (`meta.feature` on any
 * matched record), or null when the route is open.
 *
 * `meta.windDown` marks a feature's page that stays reachable without the feature, so existing
 * records can still be read and closed out (DESIGN §8.2 wind-down rule): the router lets it open,
 * only the menu hides it — pass `{ includeWindDown: true }` to count it as locked there.
 */
export function lockedFeatureOf(route, { includeWindDown = false } = {}) {
  const matched = route && route.matched;
  if (!matched) return null;
  for (let i = 0; i < matched.length; i++) {
    const meta = (matched[i] && matched[i].meta) || {};
    const feature = meta.feature || (includeWindDown ? meta.windDown : null);
    if (feature && !hasFeature(feature)) return feature;
  }
  return null;
}

// First paint: pick up what the last session stored — for this tenant only.
(function restorePersisted() {
  let stored = null;
  try {
    stored = JSON.parse(localStorage.getItem(ENTITLEMENTS_STORAGE_KEY) || "null");
  } catch (e) {
    stored = null;
  }
  if (!stored || typeof stored !== "object" || typeof stored.tenant !== "string" || stored.tenant !== currentTenant()) return;
  setState(stored.supported === true, stored.snapshot, { persist: false, emit: false });
})();
